<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import CreateContactForm from '@/components/CreateContactForm.vue'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/foundation/dialog'
import { setCompanyMember } from '@/service/companies'
import { createContact } from '@/service/users'

const props = defineProps<{
  open: boolean
  companyId: string | null
  companyName: string | null
}>()

const emit = defineEmits(['update:open', 'contactCreated'])

const internalOpen = ref(props.open)
const createContactFormRef = ref<InstanceType<typeof CreateContactForm> | null>(null)
const isSubmitting = ref(false)

watch(
  () => props.open,
  (newVal) => {
    internalOpen.value = newVal
    if (!newVal) {
      isSubmitting.value = false
      createContactFormRef.value?.resetInternalLoading()
    }
  },
)

const dialogTitle = computed(() => {
  return props.companyName ? `Create new contact for ${props.companyName} company` : 'Create new contact'
})

const formSubmitOverride = async (formData: Record<string, any>): Promise<void> => {
  if (!props.companyId) {
    console.error('Company ID is missing for contact creation.')
    createContactFormRef.value?.resetInternalLoading()
    throw new Error('Company ID is missing during contact creation process.')
  }
  const currentCompanyId = props.companyId

  isSubmitting.value = true

  // formData.phone is now the e164 string directly from CreateContactForm, or undefined/null
  const phoneToSubmit = formData.phone || null

  return new Promise((resolve, reject) => {
    const { onSuccess: onCreateUserSuccess, onError: onCreateUserError } = createContact({
      name: formData.fullName,
      username: formData.username,
      role: formData.role.id,
      email: formData.email,
      phone: phoneToSubmit, // Pass the e164 string or null
    })

    onCreateUserSuccess((newUser: any) => {
      if (!newUser || !newUser.id) {
        console.error('User creation failed or user data is invalid/missing ID (dialog - direct). Response:', newUser)
        const err = new Error(`User creation failed. Server responded: ${JSON.stringify(newUser)}`)

        isSubmitting.value = false
        reject(err)

        return
      }

      const { onSuccess: onSetMemberSuccess, onError: onSetMemberError } = setCompanyMember(currentCompanyId, {
        id: newUser.id,
      })

      onSetMemberSuccess(() => {
        emit('contactCreated', { ...newUser, fullName: newUser.name })
        closeDialog(true)
        isSubmitting.value = false
        resolve()
      })

      onSetMemberError((error: any) => {
        console.error('Error in setCompanyMember (dialog - direct):', error)
        isSubmitting.value = false
        reject(error)
      })
    })

    onCreateUserError((error: any) => {
      console.error('Error in createUser (dialog - direct):', error)
      isSubmitting.value = false
      reject(error)
    })
  })
}

const closeDialog = (created = false) => {
  internalOpen.value = false
  emit('update:open', false)
  if (!created) {
    createContactFormRef.value?.resetInternalLoading()
  }
}

// This handler can be used for both explicit close and discard via DialogCheck
const handleFormShouldCloseOrBeDiscarded = () => {
  closeDialog(false) // 'false' because it's not a successful creation
}
</script>

<template>
  <Dialog :open="internalOpen" @update:open="closeDialog(false)">
    <DialogContent
      class="sm:max-w-[600px] md:max-w-[700px] lg:max-w-[800px]"
      @escape-key-down="() => closeDialog(false)"
      @interact-outside="() => closeDialog(false)"
    >
      <DialogHeader>
        <DialogTitle>{{ dialogTitle }}</DialogTitle>
        <DialogDescription> Fill in the details to create a new contact. </DialogDescription>
      </DialogHeader>

      <div class="py-4">
        <CreateContactForm
          ref="createContactFormRef"
          :company-id="companyId"
          :contact="null"
          :is-parent-submitting="isSubmitting"
          :override-submit-handler="formSubmitOverride"
          @close-dialog="handleFormShouldCloseOrBeDiscarded"
          @dialog-should-close="handleFormShouldCloseOrBeDiscarded"
        />
      </div>
    </DialogContent>
  </Dialog>
</template>
